import React, { useState, useEffect } from "react";
import "./NewPatientRegistration.css";

const ProceduresMaster = ({ screenAction, triggerSave }) => {
  const [isDisabled, setIsDisabled] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [procList, setProcList] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);

  const initialFormState = {
    proc_id: "",
    proc_name: "",
    surgery_location: "",
    duration: "",
    rate: "",
    status: "Active",
    _isExisting: false
  };

  const [formData, setFormData] = useState(initialFormState);

  // --- 1. SEARCH LOGIC ---
  const searchProcedures = async (text) => {
    setSearchText(text);
    if (text.length < 1) {
      setProcList([]);
      setShowDropdown(false);
      return;
    }
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/procedures/search?q=${text}&employee_id=${user?.id}`);
      const data = await res.json();
      setProcList(data);
      setShowDropdown(true);
    } catch (err) {
      console.error("Search error:", err);
    }
  };

  const selectProc = (proc) => {
    setFormData({ ...proc, _isExisting: true });
    setSearchText(`${proc.proc_id} | ${proc.proc_name}`);
    setShowDropdown(false);
    if (screenAction === "edit") setIsDisabled(false);
    if (screenAction === "view") setIsDisabled(true);
  };

  // --- 2. FORM ACTIONS ---
  const clearForm = async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/procedures?employee_id=${user?.id}`);
      const allProcs = await res.json();
      const lastId = Math.max(...allProcs.map(p => parseInt(p.proc_id) || 0), 0);
      
      setFormData({
        ...initialFormState,
        proc_id: (lastId + 1).toString()
      });
    } catch (err) {
      setFormData({ ...initialFormState });
    }
    setSearchText("");
    setIsDisabled(false);
  };

  useEffect(() => {
    if (triggerSave > 0 && screenAction === "save") handleSubmit();
  }, [triggerSave]);

  useEffect(() => {
    if (screenAction === "add") clearForm();
    if (screenAction === "edit") setIsDisabled(false);
    if (screenAction === "view") setIsDisabled(true);
    if (screenAction === "refresh") clearForm();
    if (screenAction === "print") printForm();
  }, [screenAction]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // --- 3. SAVE LOGIC ---
  const handleSubmit = async () => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!formData.proc_name || !formData.rate) {
      return alert("Procedure Name and Rate are required!");
    }

    const payload = { ...formData, created_by: user?.id };
    delete payload._isExisting;

    const method = formData._isExisting ? "PUT" : "POST";
    const url = method === "POST" 
      ? "https://hmsapi.emedhatechnologies.com/master/api/api/procedures" 
      : `https://hmsapi.emedhatechnologies.com/master/api/api/procedures/${formData.proc_id}`;

    try {
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        alert("Procedure Record Saved Successfully!");
        if (method === "POST") clearForm();
      } else {
        alert("Error saving record.");
      }
    } catch (err) {
      alert("Network error.");
    }
  };

// --- PRINT PROCEDURE FORM ---
const printForm = () => {
  const newWindow = window.open("", "", "width=900,height=700");
  const today = new Date().toLocaleDateString();

  const htmlContent = `
    <html>
      <head>
        <title>Procedure Profile - ${formData.proc_name}</title>
        <style>
          body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; color: #333; margin: 0; padding: 40px; }
          .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #2980b9; padding-bottom: 10px; margin-bottom: 20px; }
          .hospital-info h1 { margin: 0; color: #2c3e50; font-size: 24px; text-transform: uppercase; }
          .hospital-info p { margin: 2px 0; font-size: 12px; color: #7f8c8d; }
          .report-title { text-align: right; }
          .report-title h2 { margin: 0; color: #2980b9; font-size: 18px; }
          .section-header { background: #ecf0f1; padding: 8px 12px; font-weight: bold; font-size: 14px; border-left: 4px solid #2980b9; margin-top: 20px; margin-bottom: 10px; text-transform: uppercase; }
          .data-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; margin-bottom: 10px; }
          .data-item { font-size: 13px; display: flex; border-bottom: 1px dotted #ccc; padding: 6px 0; }
          .label { font-weight: 600; width: 180px; color: #555; }
          .value { flex: 1; color: #000; font-weight: 500; }
          .footer { margin-top: 50px; font-size: 10px; text-align: center; color: #95a5a6; border-top: 1px solid #eee; padding-top: 10px; }
        </style>
      </head>

      <body onload="window.print(); window.close();">

        <div class="header">
          <div class="hospital-info">
            <h1>EMEDHA CARE HOSPITAL</h1>
            <p>Procedure / Surgery Master Record</p>
          </div>
          <div class="report-title">
            <h2>PROCEDURE PROFILE</h2>
            <p>Print Date: ${today}</p>
          </div>
        </div>

        <div class="section-header">Procedure Information</div>
        <div class="data-grid">
          <div class="data-item"><span class="label">Procedure ID:</span> <span class="value">${formData.proc_id}</span></div>
          <div class="data-item"><span class="label">Procedure Name:</span> <span class="value">${formData.proc_name}</span></div>
          <div class="data-item"><span class="label">Surgery Location:</span> <span class="value">${formData.surgery_location || 'N/A'}</span></div>
          <div class="data-item"><span class="label">Estimated Duration:</span> <span class="value">${formData.duration || 'N/A'}</span></div>
          <div class="data-item"><span class="label">Status:</span> <span class="value">${formData.status}</span></div>
        </div>

        <div class="section-header">Billing Details</div>
        <div class="data-grid">
          <div class="data-item">
            <span class="label">Standard Rate:</span> 
            <span class="value">₹ ${parseFloat(formData.rate || 0).toFixed(2)}</span>
          </div>
        </div>

        <div class="footer">
          System Generated Report - Emedha HMS
        </div>

      </body>
    </html>
  `;

  newWindow.document.write(htmlContent);
  newWindow.document.close();
};


  return (
    <div className="registration-container fade-in">
      {(screenAction === "edit" || screenAction === "view") && (
        <div className="patient-search">
          <label>Search Procedures</label>
          <input
            type="text"
            value={searchText}
            placeholder="Search by ID or Procedure Name..."
            onChange={(e) => searchProcedures(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && procList.length === 1) selectProc(procList[0]);
            }}
            autoFocus
          />
          {showDropdown && procList.length > 0 && (
            <div className="patient-dropdown">
              {procList.map((p) => (
                <div key={p.proc_id} className="patient-item" onClick={() => selectProc(p)}>
                  {p.proc_id} | {p.proc_name}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <form className="luxury-form">
        <div className="form-section-header">
          <h3>Procedure Identification</h3>
        </div>
        <div className="form-grid">
          <div className="input-box read-only">
            <label>Procedure ID</label>
            <input type="text" value={formData.proc_id} readOnly placeholder="Auto Generated" />
          </div>
          <div className="input-box">
            <label>Procedure Name <span style={{color:'red'}}>*</span></label>
            <input type="text" name="proc_name" value={formData.proc_name} onChange={handleChange} disabled={isDisabled} placeholder="e.g., Appendectomy" />
          </div>
          <div className="input-box">
            <label>Surgery Location</label>
            <select name="surgery_location" value={formData.surgery_location} onChange={handleChange} disabled={isDisabled}>
              <option value="">Select Location</option>
              <option value="OT1">Operation Theater 1 (OT1)</option>
              <option value="OT2">Operation Theater 2 (OT2)</option>
              <option value="Minor OT">Minor OT</option>
              <option value="Cath Lab">Cath Lab</option>
            </select>
          </div>
          <div className="input-box">
            <label>Status</label>
            <select name="status" value={formData.status} onChange={handleChange} disabled={isDisabled}>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
        </div>

        <div className="form-section-header">
          <h3>Operational Details & Billing</h3>
        </div>
        <div className="form-grid">
          <div className="input-box">
            <label>Est. Duration (e.g., 60 mins)</label>
            <input type="text" name="duration" value={formData.duration} onChange={handleChange} disabled={isDisabled} placeholder="Standard time for procedure" />
          </div>
          <div className="input-box">
            <label>Standard Rate (₹) <span style={{color:'red'}}>*</span></label>
            <input type="number" name="rate" value={formData.rate} onChange={handleChange} disabled={isDisabled} placeholder="0.00" />
          </div>
        </div>
      </form>
    </div>
  );
};

export default ProceduresMaster;